import clsx from 'clsx';
import { ReactNode, useMemo } from 'react';
import { BackSection } from 'src/components/common/BackSection';
import { useCatchUpParams } from 'src/components/Listings/InventoryEventPage/InventoryEventPage.hooks';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import {
  OnSaleEventsContextProvider,
  useOnSaleEventsContext,
} from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useAutoCompMapSectionHandler } from 'src/hooks/useAutoCompMapSectionHandler';
import { LockedNewIcon } from 'src/svgs';
import { IconsFill } from 'src/svgs/Viagogo';
import { CatchUpModeSource, EventMetricsTimestamp } from 'src/WebApiController';

import {
  ON_SALE_EVENT_METRICS_DIMENSION_TO_CID,
  ON_SALE_EVENT_METRICS_TO_CID,
} from './OnSaleEvent.constants';
import * as styles from './OnSaleEvent.css';
import { OnSaleEventMetricsName } from './OnSaleEvent.types';
import { OnSaleEventCatchUpBar } from './OnSaleEventCatchUp';
import { OnSaleEventDetails } from './OnSaleEventDetails';
import { OnSaleEventHeatMap } from './OnSaleEventHeatMap';

export const OnSaleEventPage = ({
  eventId,
  returnUrl,
}: {
  eventId: number;
  returnUrl?: string;
}) => {
  const { viagVirtualIdsList, activeViagVirtualId, isCatchupMode } =
    useCatchUpParams(CatchUpModeSource.OnSaleEvents);

  const eventIds = useMemo(
    () =>
      (viagVirtualIdsList ?? [])
        .map((id) => Number(id))
        .filter((id) => !isNaN(id)),
    [viagVirtualIdsList]
  );

  const activeEventId = useMemo(() => {
    const activeFromCatchUpMode = Number(activeViagVirtualId);
    if (isNaN(activeFromCatchUpMode)) {
      return eventId;
    }
    return activeFromCatchUpMode;
  }, [activeViagVirtualId, eventId]);

  return (
    <OnSaleEventsContextProvider
      eventIds={eventIds}
      activeEventId={activeEventId}
    >
      <OnSaleEventPageContentMapAndMetricsWrapper
        eventId={eventId}
        returnUrl={returnUrl}
        isCatchupMode={isCatchupMode}
      />
    </OnSaleEventsContextProvider>
  );
};

type InventoryEventContentProps = {
  eventId: number;
  returnUrl?: string;
  isCatchupMode?: boolean;
};

const OnSaleEventPageContentMapAndMetricsWrapper = ({
  eventId,
  returnUrl,
  isCatchupMode,
}: InventoryEventContentProps) => {
  const { isLoading, event } = useOnSaleEventsContext();

  if (isLoading || !event) {
    return <PosSpinner />;
  }

  return (
    <EventMapContextProvider event={event.event} venueMapOnly>
      <Stack direction="column" width="full" height="full">
        <OnSaleEventPageContent
          eventId={eventId}
          returnUrl={returnUrl}
          isCatchupMode={isCatchupMode}
        />
        <OnSaleEventCatchUpBar />
      </Stack>
    </EventMapContextProvider>
  );
};

const OnSaleEventPageContent = ({
  eventId,
  returnUrl,
  isCatchupMode,
}: {
  eventId: number;
  returnUrl?: string;
  isCatchupMode?: boolean;
}) => {
  const {
    metricsName,
    setMetricsName,
    timePeriod,
    onUpdateTimePeriod,
    onSaleEventAccess,
  } = useOnSaleEventsContext();

  const { isDarkMode } = useSiteTheme();

  const [dimensionDisabled, dimensionPostfixIcon] = useMemo(() => {
    const optionDisabled = {} as Record<string, boolean>;
    const optionPostfixicon = {} as Record<string, ReactNode>;
    for (const [dimension, _] of Object.entries(
      ON_SALE_EVENT_METRICS_DIMENSION_TO_CID
    )) {
      if (
        !onSaleEventAccess?.recentViewed &&
        dimension !== EventMetricsTimestamp.LastWeek
      ) {
        optionDisabled[dimension] = true;
        optionPostfixicon[dimension] = (
          <LockedNewIcon
            size={vars.iconSize.s}
            stroke={IconsFill.currentColor}
          />
        );
      } else {
        optionDisabled[dimension] = false;
        optionPostfixicon[dimension] = undefined;
      }
    }
    return [optionDisabled, optionPostfixicon];
  }, [onSaleEventAccess?.recentViewed]);

  const {
    selectedSectionIds,
    selectedSectionData,
    onSectionClicked,
    onToggleMirrors,
    onSelectedSectionsChange,
  } = useAutoCompMapSectionHandler(() => {}, !onSaleEventAccess?.recentViewed);

  const [metricsNameDisabled, metricsNamePostfixIcon] = useMemo(() => {
    const optionDisabled = {} as Record<string, boolean>;
    const optionPostfixicon = {} as Record<string, ReactNode>;
    for (const [dimension, _] of Object.entries(ON_SALE_EVENT_METRICS_TO_CID)) {
      if (
        !onSaleEventAccess?.recentViewed &&
        dimension !== OnSaleEventMetricsName.Clicks
      ) {
        optionDisabled[dimension] = true;
        optionPostfixicon[dimension] = (
          <LockedNewIcon
            size={vars.iconSize.s}
            stroke={IconsFill.currentColor}
          />
        );
      } else {
        optionDisabled[dimension] = false;
        optionPostfixicon[dimension] = undefined;
      }
    }
    return [optionDisabled, optionPostfixicon];
  }, [onSaleEventAccess?.recentViewed]);

  return (
    <Stack
      direction="row"
      width="full"
      style={{ flex: '1 1 auto', minHeight: 0 }}
    >
      <Stack
        className={styles.onSalePageLeftContainer}
        width="full"
        height="full"
        direction="column"
      >
        <Stack
          width="full"
          gap="m"
          alignItems="center"
          className={styles.onSalePageHeader}
        >
          <BackSection
            returnUrl={
              isCatchupMode ? '/discovery' : (returnUrl ?? '/discovery')
            }
            returnState={{
              fromViagVirtId: eventId.toString(),
            }}
          />
          <PosEnumSelect
            className={styles.metricsDropdown}
            value={timePeriod}
            valueOptionsContent={ON_SALE_EVENT_METRICS_DIMENSION_TO_CID}
            valueOptionsDisabled={dimensionDisabled}
            valueOptionsPostfixIcon={dimensionPostfixIcon}
            onChange={(value) =>
              onUpdateTimePeriod(value as EventMetricsTimestamp)
            }
          />
          <PosEnumSelect
            className={styles.metricsDropdown}
            value={metricsName}
            valueOptionsContent={ON_SALE_EVENT_METRICS_TO_CID}
            valueOptionsDisabled={metricsNameDisabled}
            valueOptionsPostfixIcon={metricsNamePostfixIcon}
            onChange={(value) =>
              setMetricsName(value as OnSaleEventMetricsName)
            }
          />
        </Stack>
        <div
          className={clsx(styles.venueMapContainer, {
            [styles.venueMapContainerLocked]: !onSaleEventAccess?.recentViewed,
            [styles.venueMapContainerUnlockedLight]:
              onSaleEventAccess?.recentViewed && !isDarkMode,
          })}
        >
          <OnSaleEventHeatMap
            selectedSectionIds={selectedSectionIds}
            onSectionClicked={onSectionClicked}
            onToggleMirrors={onToggleMirrors}
            onSelectedSectionsChange={onSelectedSectionsChange}
            isLocked={!onSaleEventAccess?.recentViewed}
          />
        </div>
      </Stack>
      <Stack
        className={styles.onSalePageRightContainer}
        width="full"
        height="full"
        direction="column"
      >
        <OnSaleEventDetails
          selectedSections={selectedSectionData}
          onSelectedSectionsChange={onSelectedSectionsChange}
        />
      </Stack>
    </Stack>
  );
};
